import type { TowerConfig } from '../types';

export const TOWER_CONFIGS: Record<string, TowerConfig> = {
  archer: {
    id: 'archer',
    name: '箭塔',
    cost: 80,
    range: 3,
    damage: 8,
    fireRate: 1.2,
    projectileType: 'projectile',
    damageType: 'physical',
    targetFlags: ['ground'],
    color: '#8bc34a',
    role: '单体',
    description: '便宜稳定的地面单体输出，适合前期铺火力。',
    special: '攻速快，升级后射程和伤害都均衡提升。',
    usage: '用来清理史莱姆、狼等普通地面敌人。',
    upgrades: [
      { level: 2, cost: 120, damageMultiplier: 1.6, rangeBonus: 0.5, fireRateMultiplier: 1.1 },
      { level: 3, cost: 200, damageMultiplier: 2.4, rangeBonus: 1.0, fireRateMultiplier: 1.2 },
    ],
  },
  cannon: {
    id: 'cannon',
    name: '炮塔',
    cost: 150,
    range: 2.5,
    damage: 25,
    fireRate: 0.5,
    projectileType: 'aoe',
    damageType: 'physical',
    targetFlags: ['ground'],
    color: '#ff9800',
    role: '范围',
    description: '慢速高伤炮弹，命中点附近敌人一起受伤。',
    special: '溅射伤害，敌人扎堆或被减速时收益最高。',
    usage: '克制密集小怪和高护甲前的普通集群。',
    upgrades: [
      { level: 2, cost: 220, damageMultiplier: 1.5, rangeBonus: 0.5, fireRateMultiplier: 1.1 },
      { level: 3, cost: 350, damageMultiplier: 2.2, rangeBonus: 0.5, fireRateMultiplier: 1.2 },
    ],
  },
  ice: {
    id: 'ice',
    name: '冰塔',
    cost: 120,
    range: 3,
    damage: 4,
    fireRate: 0.8,
    projectileType: 'projectile',
    damageType: 'ice',
    targetFlags: ['ground'],
    color: '#00bcd4',
    role: '减速',
    description: '低伤害控制塔，让地面敌人移动变慢。',
    special: '命中后施加减速，给炮塔和箭塔争取输出时间。',
    usage: '放在转角、长路和炮塔附近最有效。',
    upgrades: [
      { level: 2, cost: 180, damageMultiplier: 1.5, rangeBonus: 0.5, fireRateMultiplier: 1.1 },
      { level: 3, cost: 280, damageMultiplier: 2.0, rangeBonus: 1.0, fireRateMultiplier: 1.2 },
    ],
  },
  lightning: {
    id: 'lightning',
    name: '雷塔',
    cost: 180,
    range: 3.5,
    damage: 15,
    fireRate: 0.7,
    projectileType: 'hitscan',
    damageType: 'lightning',
    targetFlags: ['ground', 'flying'],
    color: '#9c27b0',
    role: '对空',
    description: '即时命中的雷电塔，可攻击地面和飞行敌人。',
    special: '命中附带短暂眩晕，适合拦截漏网和飞行单位。',
    usage: '飞行波次前至少准备一两座。',
    upgrades: [
      { level: 2, cost: 260, damageMultiplier: 1.6, rangeBonus: 0.5, fireRateMultiplier: 1.1 },
      { level: 3, cost: 400, damageMultiplier: 2.3, rangeBonus: 0.5, fireRateMultiplier: 1.2 },
    ],
  },
  poison: {
    id: 'poison',
    name: '毒塔',
    cost: 140,
    range: 3,
    damage: 6,
    fireRate: 0.9,
    projectileType: 'projectile',
    damageType: 'poison',
    targetFlags: ['ground', 'flying'],
    color: '#4caf50',
    role: '毒伤',
    description: '持续伤害塔，适合处理血厚或抗物理敌人。',
    special: '命中后造成中毒 DOT，可攻击飞行敌人。',
    usage: '克制兽人、盾兵、Boss 等高血量目标。',
    upgrades: [
      { level: 2, cost: 210, damageMultiplier: 1.4, rangeBonus: 0.5, fireRateMultiplier: 1.1 },
      { level: 3, cost: 330, damageMultiplier: 1.9, rangeBonus: 1.0, fireRateMultiplier: 1.2 },
    ],
  },
  sniper: {
    id: 'sniper',
    name: '狙击塔',
    cost: 250,
    range: 6,
    damage: 60,
    fireRate: 0.3,
    projectileType: 'hitscan',
    damageType: 'physical',
    targetFlags: ['ground', 'flying'],
    color: '#607d8b',
    role: '狙杀',
    description: '超远射程高单发伤害，攻速较慢。',
    special: '即时命中且可对空，适合点杀强敌和漏怪。',
    usage: '放在地图中部或高覆盖位置收益最大。',
    upgrades: [
      { level: 2, cost: 360, damageMultiplier: 1.7, rangeBonus: 0.8, fireRateMultiplier: 1.1 },
      { level: 3, cost: 560, damageMultiplier: 2.6, rangeBonus: 1.2, fireRateMultiplier: 1.2 },
    ],
  },
  support: {
    id: 'support',
    name: '增益塔',
    cost: 160,
    range: 4,
    damage: 0,
    fireRate: 0,
    projectileType: 'projectile',
    damageType: 'true',
    targetFlags: [],
    color: '#ffeb3b',
    role: '增益',
    description: '不攻击敌人，强化周围防御塔。',
    special: '范围内友方塔获得伤害、射程和攻速加成。',
    usage: '放在塔群中心，服务多个输出塔。',
    upgrades: [
      { level: 2, cost: 240, damageMultiplier: 1, rangeBonus: 1, fireRateMultiplier: 1 },
      { level: 3, cost: 380, damageMultiplier: 1, rangeBonus: 1.5, fireRateMultiplier: 1 },
    ],
  },
  barracks: {
    id: 'barracks',
    name: '兵营',
    cost: 200,
    range: 3,
    damage: 30,
    fireRate: 0.4,
    projectileType: 'projectile',
    damageType: 'physical',
    targetFlags: ['ground'],
    color: '#795548',
    role: '控制',
    description: '重型地面控制塔，攻击会震晕附近敌人。',
    special: '命中造成小范围伤害并附带眩晕。',
    usage: '适合守转角、核心前和敌人汇合点。',
    upgrades: [
      { level: 2, cost: 300, damageMultiplier: 1.5, rangeBonus: 0.5, fireRateMultiplier: 1.1 },
      { level: 3, cost: 480, damageMultiplier: 2.2, rangeBonus: 1, fireRateMultiplier: 1.2 },
    ],
  },
};
